import React from 'react';
import { CalculationResult } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

interface ResultsDisplayProps {
  result: CalculationResult;
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ result }) => {
  const { t } = useLanguage();
  
  const gaugeValue = Math.min(result.density, 50); 
  const remaining = 50 - gaugeValue;

  const getColor = (density: number) => {
    if (density < 6) return '#ef4444'; // Red (Expensive class)
    if (density < 12) return '#f59e0b'; // Amber
    if (density < 22) return '#3b82f6'; // Blue
    return '#10b981'; // Green (Cheaper class)
  };

  const chartColor = getColor(result.density);

  const data = {
    labels: [t('density'), 'Scale'],
    datasets: [
      {
        data: [gaugeValue, remaining],
        backgroundColor: [chartColor, '#e2e8f0'],
        borderColor: ['#ffffff', '#ffffff'],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    rotation: -90,
    circumference: 180,
    cutout: '60%',
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden sticky top-6">
      <div className="bg-slate-900 px-6 py-4 border-b border-slate-800 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-indigo-400">
            <path fillRule="evenodd" d="M2.25 13.5a8.25 8.25 0 0 1 8.25-8.25.75.75 0 0 1 .75.75v6.75H18a.75.75 0 0 1 .75.75 8.25 8.25 0 0 1-16.5 0Z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M12.75 3a.75.75 0 0 1 .75-.75 8.25 8.25 0 0 1 8.25 8.25.75.75 0 0 1-.75.75h-7.5a.75.75 0 0 1-.75-.75V3Z" clipRule="evenodd" />
          </svg>
          {t('calcResults')}
        </h2>
        <span className="text-xs font-mono bg-slate-800 text-slate-300 px-2 py-1 rounded">
            {t('livePreview')}
        </span>
      </div>

      <div className="p-6 space-y-8">
        {/* Primary Metric: Class */}
        <div className="text-center">
             <div className="inline-flex flex-col items-center p-6 bg-slate-50 rounded-2xl border-2 border-slate-100 relative w-full">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">{t('estClass')}</span>
                <span className="text-6xl font-black text-slate-900 tracking-tight">{result.estimatedClass}</span>
                <div 
                    className="absolute top-4 right-4 w-3 h-3 rounded-full animate-pulse"
                    style={{ backgroundColor: chartColor }}
                    title="Density Indicator"
                ></div>
             </div>
        </div>

        {/* Secondary Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-500 font-medium uppercase mb-1">{t('density')}</p>
                <p className="text-2xl font-bold text-slate-800">{result.density.toFixed(2)} <span className="text-sm font-normal text-slate-400">pcf</span></p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-500 font-medium uppercase mb-1">{t('linearFeet')}</p>
                <p className={`text-2xl font-bold ${result.linearFeet > 12 ? 'text-red-600' : 'text-slate-800'}`}>
                    {result.linearFeet.toFixed(1)} <span className="text-sm font-normal text-slate-400">ft</span>
                </p>
                {result.linearFeet > 12 && <p className="text-[10px] text-red-500 leading-tight mt-1">{t('capacityWarning')}</p>}
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-500 font-medium uppercase mb-1">{t('totalWeight')}</p>
                <p className="text-lg font-bold text-slate-800">{result.totalWeight} <span className="text-sm font-normal text-slate-400">lbs</span></p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-500 font-medium uppercase mb-1">{t('totalVol')}</p>
                <p className="text-lg font-bold text-slate-800">{result.totalCubicFeet.toFixed(2)} <span className="text-sm font-normal text-slate-400">ft³</span></p>
            </div>
        </div>

        {/* Visual Gauge - Replaced Recharts with Chart.js */}
        <div className="relative w-full h-[160px] mt-4 flex justify-center">
            {/* Wrapper div with fixed height to prevent Chart.js crash */}
            <div className="relative w-full h-full max-w-[300px]">
                <Pie data={data} options={options} />
            </div>
            <div className="absolute bottom-0 w-full text-center">
                 <span className="text-xs text-slate-400 font-medium">{t('densityScale')}</span>
            </div>
        </div>
      </div>
      
      {/* Action Bar */}
      <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-center no-print">
         <button 
           onClick={() => window.print()} 
           className="text-sm text-slate-600 hover:text-indigo-600 font-medium flex items-center gap-2 transition-colors"
         >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M5 2.75C5 1.784 5.784 1 6.75 1h6.5c.966 0 1.75.784 1.75 1.75v3.552c.377.046.752.097 1.126.153A2.212 2.212 0 0 1 18 8.653v4.097A2.25 2.25 0 0 1 15.75 15h-.241l.305 1.984A1.75 1.75 0 0 1 14.084 19H5.915a1.75 1.75 0 0 1-1.73-2.016L4.492 15H4.25A2.25 2.25 0 0 1 2 12.75V8.653c0-1.082.775-2.034 1.874-2.198.374-.056.75-.107 1.127-.153V2.75ZM6.75 2.5h6.5v3.448a48.4 48.4 0 0 0-6.5 0V2.5Zm3.75 14.25a.75.75 0 0 0 1.5 0v-2.25a.75.75 0 0 0-1.5 0v2.25Z" clipRule="evenodd" />
            </svg>
            {t('printBtn')}
         </button>
      </div>
    </div>
  );
};